import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Loader2 } from "lucide-react";
import { fetchRecipes } from "../../lib/api";

export const Recipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    fetchRecipes()
      .then((data) => {
        if (!ignore) setRecipes(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, []);

  const featured = recipes.slice(0, 3);

  return (
    <section
      id="recipes"
      data-testid="recipes-section"
      className="relative py-24 lg:py-32 bg-[#2A4B3C] text-mo-cream overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "4px 4px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D99026] mb-4">
              — From our kitchen
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Recipes that <span className="italic">taste of home.</span>
            </h2>
          </div>
          <p className="max-w-md text-[#F9F6F0]/80">
            Cook along — every recipe tells you which Maati tin it was built for.
          </p>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-[#F9F6F0]/70">
            <Loader2 className="animate-spin mr-3" size={18} /> Brewing recipes…
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {featured.map((r) => (
              <Link
                key={r.slug}
                to={`/recipes/${r.slug}`}
                data-testid={`recipe-${r.slug}`}
                className="group"
              >
                <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                  <img
                    src={r.image}
                    alt={r.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2A4B3C] via-[#2A4B3C]/20 to-transparent" />
                  <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-mo-cream text-mo-green text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
                    {r.spice_name}
                  </div>
                  <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-mo-cream/90 flex items-center justify-center text-mo-green group-hover:bg-[#D99026] transition-colors duration-300">
                    <ArrowUpRight size={18} />
                  </div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#D99026] mb-2">
                      {r.kicker}
                    </div>
                    <h3 className="font-serif-display text-2xl leading-tight mb-3">
                      {r.name}
                    </h3>
                    <p className="text-sm text-[#F9F6F0]/85 line-clamp-3">{r.intro}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-14 flex justify-center">
          <Link
            to="/recipes"
            data-testid="recipes-view-all"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#D99026] text-mo-ink font-medium hover:bg-[#F9F6F0] transition-all duration-300 hover:-translate-y-0.5"
          >
            Explore all recipes
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};
